import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Lands here at `{origin}/auth/google/callback#session_id=...` after Google
 * sign-in via Emergent-managed Auth. Exchanges the session_id for our own
 * app JWT (login intent) or links Google to the already-logged-in user
 * (link intent, detected via `?intent=link` — set when the redirect was
 * started from Settings > Connected Accounts).
 */
export default function GoogleAuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithToken, refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    const intent = new URLSearchParams(location.search).get("intent");

    if (!sessionId) {
      setError("Missing Google session — please try signing in again.");
      return;
    }

    (async () => {
      try {
        if (intent === "link") {
          await api.post("/auth/google/link", { session_id: sessionId });
          await refresh();
          navigate("/settings/account", { replace: true });
        } else {
          const { data } = await api.post("/auth/google/callback", { session_id: sessionId });
          if (data.mfa_required) {
            navigate("/login", { replace: true, state: { mfaToken: data.mfa_token, methods: data.methods } });
            return;
          }
          loginWithToken(data.access_token, data.user);
          navigate("/", { replace: true });
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Google sign-in failed. Please try again.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="google-auth-callback">
      {error ? (
        <div className="max-w-sm text-center space-y-3">
          <p className="text-sm text-destructive font-medium" data-testid="google-auth-callback-error">{error}</p>
          <button
            className="text-sm text-primary underline"
            onClick={() => navigate("/login", { replace: true })}
            data-testid="google-auth-callback-back-link"
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      )}
    </div>
  );
}
